import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PersonCatalogKind } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { Button, Field } from '@/components/ui';

const NEW_OPTION = '__new__';

/**
 * Profesión / Ocupación / Sector Económico — a `<select>` over the tenant's
 * own picklist (`PersonCatalogEntry`, kind-scoped), with an inline
 * "+ Agregar nueva…" option for whoever can edit a person
 * (`people.person.update`) rather than a separate catalog-management
 * screen nobody asked for. Picking it reveals a text input; submitting it
 * creates the entry and selects it immediately.
 */
export function CatalogPicker({
  kind,
  label,
  value,
  onChange,
}: {
  readonly kind: PersonCatalogKind;
  readonly label: string;
  readonly value: string;
  readonly onChange: (id: string) => void;
}): ReactNode {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const options = useQuery({
    queryKey: ['people', 'catalog', kind],
    queryFn: ({ signal }) => peopleApi.listCatalog(kind, false, signal),
  });

  const create = useMutation({
    mutationFn: (name: string) => peopleApi.createCatalogEntry(kind, name),
    onSuccess: (entry) => {
      queryClient.setQueryData(['people', 'catalog', kind], (previous: typeof options.data) => [
        ...(previous ?? []),
        entry,
      ]);
      setAdding(false);
      setDraft('');
      onChange(entry.id);
    },
  });

  const list = options.data ?? [];
  const id = `catalog-${kind}`;

  if (adding) {
    return (
      <div className="inline-add-row">
        <Field
          label={label}
          name={id}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          autoFocus
        />
        <Button
          type="button"
          variant="secondary"
          loading={create.isPending}
          onClick={() => draft.trim() && create.mutate(draft.trim())}
        >
          {t('common.actions.save')}
        </Button>
        <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
          {t('common.actions.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="field-input"
        value={value}
        onChange={(event) => {
          if (event.target.value === NEW_OPTION) {
            setAdding(true);
            return;
          }
          onChange(event.target.value);
        }}
      >
        <option value="">{t('common.labels.none')}</option>
        {list.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.name}
          </option>
        ))}
        {can('people.person.update') && (
          <option value={NEW_OPTION}>{t('common.actions.addNew')}</option>
        )}
      </select>
    </div>
  );
}
